import { arrRemoveValue, isDisplayed, parseHtmlToDom, touchesElement, touchesViewport } from 'js/tools';

/**
 * Handles lazy loading and responsive images
 */
function MediaLoader() {
    const refs = {
        get responsiveImageElements() {
            return document.querySelectorAll('[data-responsive-image]');
        },
        get lazyLoadElements() {
            return document.querySelectorAll('[data-lazyload="auto"]');
        }
    };

    const lazyLoadConfigs = [];

    const OFFSET_RESPONSIVE_IMAGE_REPLACEMENT = 150;
    const OFFSET_LAZYLOAD = 400;

    const qualities = [160, 320, 640, 1024, 1280, 1920];

    function init() {
        prepareLazyLoadElements();

        setInterval(() => {
            lazyLoadImages();
            updateImageQualities();
        }, 500);
    }

    function addImageLoadIndicator(elm) {
        const spinner = parseHtmlToDom(`
            <div class="image-loader">
              <div class="image-loader__dot"></div>
              <div class="image-loader__dot"></div>
              <div class="image-loader__dot"></div>
            </div>
        `);

        elm.parentNode.style.position = 'relative';
        elm.parentNode.appendChild(spinner);
    }

    function prepareLazyLoadElements() {
        refs.lazyLoadElements.forEach(elm => {
            elm.style.opacity = '0';
            elm.style.transition = 'opacity .3s ease-in-out';
            addImageLoadIndicator(elm);
            elm.addEventListener('load', handleOnImageLoad);
        });
    }

    function handleOnImageLoad(e) {
        const elm = e.target;
        const imageLoader = elm.parentNode.querySelector('.image-loader');

        if (imageLoader) {
            elm.parentNode.removeChild(imageLoader);
        }

        elm.style.opacity = '1';
    }

    /**
     * Calculate target quality of image based on its own context
     * @param image
     * @returns (int) target quality
     */
    function determineTargetQuality(widthInPixels, pixelDensity) {
        const minimumQualityNeeded = widthInPixels * pixelDensity;
        const targetQuality = qualities.find(quality => quality >= minimumQualityNeeded);

        return targetQuality || 'max';
    }

    /**
     * Check current quality of image based on src naming convention
     * @param src
     * @returns (int) current quality
     */
    function determineCurrentQuality(src) {
        if (src.search(`qmax`) !== -1) {
            return 'max';
        }

        const currentQuality = qualities.find(quality => src.search(`q${quality}`) !== -1);

        return currentQuality;
    }

    /**
     * Retrieve image src of a given element. This can be the src of an image element or a background-image
     * @param image
     * @returns {string} source url
     */
    function getImageSource(responsiveImageElement) {
        const imageType = getImageType(responsiveImageElement);
        let src;

        if (imageType === 'image') {
            ({ src } = responsiveImageElement);
        } else if (imageType === 'background-image') {
            src = responsiveImageElement.style.backgroundImage.slice(4, -1).replace(/['"]/g, '');
        }

        return src;
    }

    /**
     * Check if image type. regular image OR background-image
     * @param image
     * @returns {string} imageType
     */
    function getImageType(responsiveImageElement) {
        return responsiveImageElement.tagName === 'IMG' ? 'image' : 'background-image';
    }

    /**
     * Download image in background. Perform callback when done
     * @param src
     * @param cb
     */
    function downloadImage(src, cb) {
        const image = new Image();

        image.onload = () => {
            cb(true);
        };

        image.onerror = () => {
            cb(false);
        };

        image.src = src;
    }

    /**
     * Update the source of an image/background image
     * @param responsiveImageElement
     * @param targetSrc
     */
    function updateImageSrc(responsiveImageElement, targetSrc) {
        const imageType = getImageType(responsiveImageElement);

        if (imageType === 'image') {
            responsiveImageElement.src = targetSrc;
        } else if (imageType === 'background-image') {
            responsiveImageElement.style.backgroundImage = `url(${targetSrc})`;
        }

        responsiveImageElement.setAttribute('data-responsive-image', determineCurrentQuality(targetSrc));
    }

    /**
     * Update quality of all responsive images
     */
    function updateImageQualities() {
        refs.responsiveImageElements.forEach(responsiveImageElement => {
            if (touchesViewport(responsiveImageElement, OFFSET_RESPONSIVE_IMAGE_REPLACEMENT)) {
                processImageQuality(responsiveImageElement);
            }
        });
    }

    function watch(config) {
        if (
            typeof config.action !== 'undefined' &&
            typeof config.src !== 'undefined' &&
            typeof config.target !== 'undefined' &&
            typeof config.offset !== 'undefined' &&
            config.action === 'lazyload'
        ) {
            lazyLoadConfigs.push(config);
        }
    }

    function loadImage(lazyLoadElement) {
        const imageType = getImageType(lazyLoadElement);

        lazyLoadElement.removeAttribute('data-lazyload');

        if (imageType === 'image') {
            const attributesToMove = ['sizes', 'srcset', 'src'];

            attributesToMove.forEach(attribute => {
                const attributeValue = lazyLoadElement.getAttribute(`data-${attribute}`);

                if (attributeValue) {
                    lazyLoadElement.setAttribute(attribute, attributeValue);
                }
            });
        } else if (imageType === 'background-image') {
            const imageSource = lazyLoadElement.getAttribute('data-image-src');

            lazyLoadElement.style.backgroundImage = `url(${imageSource})`;
        }
    }

    function mediaWithinBoundry(srcElm, offset, targetElm) {
        if (typeof targetElm === 'undefined') {
            if (touchesViewport(srcElm, offset)) {
                return true;
            }
        } else if (touchesElement(srcElm, targetElm, offset)) {
            return true;
        }

        return false;
    }

    function lazyLoadImages() {
        refs.lazyLoadElements.forEach(lazyLoadElement => {
            if (isDisplayed(lazyLoadElement) && mediaWithinBoundry(lazyLoadElement, OFFSET_LAZYLOAD)) {
                loadImage(lazyLoadElement);
            }
        });

        lazyLoadConfigs.forEach(lazyLoadConfig => {
            if (
                isDisplayed(lazyLoadConfig.src) &&
                mediaWithinBoundry(lazyLoadConfig.src, lazyLoadConfig.offset, lazyLoadConfig.target)
            ) {
                loadImage(lazyLoadConfig.src);
                arrRemoveValue(lazyLoadConfigs, lazyLoadConfig);
            }
        });
    }

    function processImageQuality(responsiveImageElement) {
        const src = getImageSource(responsiveImageElement);
        const currentQuality = determineCurrentQuality(src);
        const targetQuality = determineTargetQuality(responsiveImageElement.offsetWidth, window.devicePixelRatio);
        const maxSrc = src.replace(`q${currentQuality}`, `qmax`);

        if (targetQuality === 'max' && currentQuality !== 'max') {
            downloadImage(maxSrc, isSuccess => {
                if (isSuccess) {
                    updateImageSrc(responsiveImageElement, maxSrc);
                }
            });
        } else if (targetQuality > currentQuality) {
            const targetSrc = src.replace(`q${currentQuality}`, `q${targetQuality}`);

            downloadImage(targetSrc, isSuccess => {
                if (isSuccess) {
                    updateImageSrc(responsiveImageElement, targetSrc);
                } else {
                    downloadImage(maxSrc, onSuccess => {
                        if (onSuccess) {
                            updateImageSrc(responsiveImageElement, maxSrc);
                        }
                    });
                }
            });
        }
    }

    init();

    return {
        loadImage,
        lazyLoadImages,
        updateImageQualities,
        watch,
        determineCurrentQuality,
        determineTargetQuality,
        mediaWithinBoundry,
        prepareLazyLoadElements
    };
}

const mediaLoader = new MediaLoader();

Object.freeze(mediaLoader);

export default mediaLoader;
